import { useEffect, useState, useCallback } from "react";
import { chListCentros, chListPuestosEmpresa, chCrearPuesto, chEditarPuesto, chBorrarPuesto } from "@/lib/prevelis";
import { toast } from "sonner";
import { Plus, Briefcase, Building, Users, Pencil, Trash2, Loader2, X, Save } from "lucide-react";

const field = "w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none text-sm";
const EMPTY = { nombre: "", centro_id: "", num_trabajadores: "", funciones: "", tareas: "", equipos: "", productos: "", observaciones: "" };

export default function SuizaPuestos({ empresaId, onCount }) {
  const [centros, setCentros] = useState([]);
  const [puestos, setPuestos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([chListCentros(empresaId), chListPuestosEmpresa(empresaId)])
      .then(([c, p]) => { setCentros(c); setPuestos(p); onCount?.(p.length); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [empresaId, onCount]);
  useEffect(() => { load(); }, [load]);

  const centroNombre = (cid) => centros.find((c) => c.id === cid)?.nombre || "—";
  const abrirNuevo = () => { setForm({ ...EMPTY, centro_id: centros[0]?.id || "" }); setEditId(null); setOpen(true); };
  const abrirEditar = (p) => { setForm({ ...EMPTY, ...p }); setEditId(p.id); setOpen(true); };
  const guardar = async () => {
    if (!(form.nombre || "").trim()) { toast.error("El nombre del puesto es obligatorio"); return; }
    if (!form.centro_id) { toast.error("Selecciona un centro"); return; }
    setSaving(true);
    try {
      if (editId) { await chEditarPuesto(editId, form); toast.success("Puesto actualizado"); }
      else { await chCrearPuesto(form.centro_id, form); toast.success("Puesto creado"); }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };
  const borrar = async (p) => {
    if (!window.confirm(`¿Eliminar el puesto "${p.nombre}"?`)) return;
    try { await chBorrarPuesto(p.id); toast.success("Puesto eliminado"); load(); }
    catch { toast.error("No se pudo eliminar"); }
  };

  const porCentro = centros.map((c) => ({ centro: c, items: puestos.filter((p) => p.centro_id === c.id) }));

  return (
    <div data-testid="ch-puestos-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="font-heading font-semibold text-lg">Puestos de trabajo</h3>
          <p className="text-sm text-slate-500">Cada puesto pertenece a un centro. Se usarán en las inspecciones y en los documentos por puesto.</p>
        </div>
        <button onClick={abrirNuevo} disabled={centros.length === 0} data-testid="ch-puesto-nuevo" className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-blue-700 text-white text-sm font-medium hover:bg-blue-800 disabled:opacity-40">
          <Plus className="w-4 h-4" /> Nuevo puesto
        </button>
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
      ) : centros.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-8 text-center text-slate-500" data-testid="ch-puestos-sin-centros">
          Primero crea al menos un centro en la pestaña «Centros».
        </div>
      ) : puestos.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-8 text-center text-slate-500" data-testid="ch-puestos-vacio">
          Aún no hay puestos. Añade el primero con «Nuevo puesto».
        </div>
      ) : (
        <div className="space-y-6" data-testid="ch-puestos-lista">
          {porCentro.filter((g) => g.items.length > 0).map((g) => (
            <div key={g.centro.id}>
              <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-slate-600">
                <Building className="w-4 h-4 text-blue-600" /> {g.centro.nombre}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {g.items.map((p) => (
                  <div key={p.id} data-testid={`ch-puesto-${p.id}`} className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center shrink-0"><Briefcase className="w-4 h-4" /></div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-slate-900 truncate">{p.nombre}</div>
                        {p.funciones && <div className="text-xs text-slate-500 line-clamp-2">{p.funciones}</div>}
                        {p.num_trabajadores !== "" && p.num_trabajadores != null && <div className="mt-1 text-xs text-slate-500 inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {p.num_trabajadores}</div>}
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => abrirEditar(p)} data-testid={`ch-puesto-editar-${p.id}`} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => borrar(p)} data-testid={`ch-puesto-borrar-${p.id}`} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="ch-puesto-dialog">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-lg">{editId ? "Editar puesto" : "Nuevo puesto"}</h3>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-slate-700">Nombre del puesto *</label>
                  <input data-testid="ch-puesto-nombre" value={form.nombre} onChange={(e) => setForm((s) => ({ ...s, nombre: e.target.value }))} className={field + " mt-1 h-11"} /></div>
                <div><label className="text-sm font-medium text-slate-700">Centro *</label>
                  <select data-testid="ch-puesto-centro" value={form.centro_id} onChange={(e) => setForm((s) => ({ ...s, centro_id: e.target.value }))} className={field + " mt-1 h-11"}>
                    <option value="">— Selecciona —</option>
                    {centros.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                  </select></div>
              </div>
              <div><label className="text-sm font-medium text-slate-700">Nº de trabajadores</label>
                <input type="number" min="0" data-testid="ch-puesto-num" value={form.num_trabajadores} onChange={(e) => setForm((s) => ({ ...s, num_trabajadores: e.target.value }))} className={field + " mt-1 h-11"} /></div>
              <div><label className="text-sm font-medium text-slate-700">Funciones principales</label>
                <textarea data-testid="ch-puesto-funciones" rows={2} value={form.funciones} onChange={(e) => setForm((s) => ({ ...s, funciones: e.target.value }))} className={field + " mt-1"} /></div>
              <div><label className="text-sm font-medium text-slate-700">Tareas habituales</label>
                <textarea data-testid="ch-puesto-tareas" rows={2} value={form.tareas} onChange={(e) => setForm((s) => ({ ...s, tareas: e.target.value }))} className={field + " mt-1"} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-slate-700">Equipos / herramientas</label>
                  <textarea data-testid="ch-puesto-equipos" rows={2} value={form.equipos} onChange={(e) => setForm((s) => ({ ...s, equipos: e.target.value }))} className={field + " mt-1"} /></div>
                <div><label className="text-sm font-medium text-slate-700">Productos utilizados</label>
                  <textarea data-testid="ch-puesto-productos" rows={2} value={form.productos} onChange={(e) => setForm((s) => ({ ...s, productos: e.target.value }))} className={field + " mt-1"} /></div>
              </div>
              <div><label className="text-sm font-medium text-slate-700">Observaciones</label>
                <textarea data-testid="ch-puesto-observaciones" rows={2} value={form.observaciones} onChange={(e) => setForm((s) => ({ ...s, observaciones: e.target.value }))} className={field + " mt-1"} /></div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setOpen(false)} className="h-11 px-4 rounded-lg border border-slate-300 font-medium hover:bg-slate-50">Cancelar</button>
              <button onClick={guardar} disabled={saving} data-testid="ch-puesto-guardar" className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-blue-700 text-white font-medium hover:bg-blue-800 disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
